import * as Sentry from '@sentry/react';
import type { ComponentType } from 'react';

import LazyError from 'src/components/Routes/LazyError';

const RELOAD_FLAG = 'u2r:lazy-reload-attempted';
const RELOAD_THROTTLE_MS = 5 * 60 * 1000;
const IMPORT_TIMEOUT_MS = 15000;
const IMPORT_TIMEOUT_MARKER = 'u2r:lazy-import-timeout';

const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }
  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    // Chrome/Edge
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Failed to load module script/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message)
  );
};

const shouldAttemptReload = (): boolean => {
  try {
    const lastReloadTimestamp = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - lastReloadTimestamp < RELOAD_THROTTLE_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    return true;
  } catch {
    // sessionStorage may be unavailable; fall through to the retry path.
    return false;
  }
};

const importWithTimeout = async <T>(importFunction: () => Promise<T>): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(IMPORT_TIMEOUT_MARKER)), IMPORT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([importFunction(), timeout]);
  } finally {
    clearTimeout(timer);
  }
};

export const retryLazy = async <T>(
  importFunction: () => Promise<{ default: ComponentType<T> }>,
  retries = 5,
  interval = 1000
): Promise<{ default: ComponentType<T> }> => {
  try {
    return await importWithTimeout(importFunction);
  } catch (error) {
    // A hung import returns the same pending promise on every retry, so fail over to the error screen.
    if (error instanceof Error && error.message === IMPORT_TIMEOUT_MARKER) {
      Sentry.captureException(error);
      return { default: LazyError as ComponentType<T> };
    }
    // Stale chunk after a deploy: the hashed file no longer exists and only a reload fixes it.
    // Throttled so a genuinely broken deploy cannot cause a reload loop, while still allowing
    // recovery from every deploy over the lifetime of a long-lived tab.
    if (isChunkLoadError(error) && typeof window !== 'undefined' && shouldAttemptReload()) {
      window.location.reload();
      return new Promise(() => undefined);
    }
    if (retries) {
      await wait(interval);
      return retryLazy(importFunction, retries - 1, interval);
    }
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
    // Static import: the fallback must never depend on fetching another (equally stale) chunk.
    return { default: LazyError as ComponentType<T> };
  }
};

const wait = (time: number) => {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise((resolve) => setTimeout(resolve, time));
};
